import {
  Game,
  GameInformationTypeEnum,
  GameMetadata,
} from "entities/game/types";
import { GameTagType } from "entities/tagTypes/types";
import { Tag } from "entities/tags/types";
import { ThemeTag } from "entities/themeTags/types";
import groupBy from "lodash/groupBy";
import { v4 as uuidv4 } from "uuid";
import { TagOption } from "widgets/ModuleTagsWidget/type";
import { GameTagBlock } from "../stores/useGameTagsData";
import { MetadataStore } from "../stores/useMetadata";

export const getGameMetadataByType = (
  type: GameInformationTypeEnum,
  metadata?: GameMetadata | null,
) => {
  return metadata?.gameInformation?.find((data) => data.type === type) ?? null;
};

export const getGameInformation = (metadataStore: MetadataStore) => [
  {
    type: GameInformationTypeEnum.VOLATILITY,
    value: metadataStore.volatility ?? "",
  },
  {
    type: GameInformationTypeEnum.RTP_EXACT,
    value: metadataStore.rtpExact ?? [],
  },
  {
    type: GameInformationTypeEnum.PAYLINES,
    value: Number(metadataStore.paylines) ?? null,
  },
  {
    type: GameInformationTypeEnum.JACKPOT_TYPE,
    value: metadataStore.jackpotType ?? [],
  },
  {
    type: GameInformationTypeEnum.REELS,
    value: Number(metadataStore.reels) ?? null,
  },
  {
    type: GameInformationTypeEnum.ROWS,
    value: Number(metadataStore.rows) ?? "",
  },
  {
    type: GameInformationTypeEnum.LANGUAGE_SUPPORT,
    value: metadataStore.languageSupport ?? [],
  },
  {
    type: GameInformationTypeEnum.RELEASE_DATE,
    value: Number(metadataStore.releaseDate) ?? "",
  },
  {
    type: GameInformationTypeEnum.AUTOPLAY,
    value: metadataStore.autoplay ?? null,
  },
  {
    type: GameInformationTypeEnum.MIN_STAKE,
    value: metadataStore.minStake ?? "",
  },
  {
    type: GameInformationTypeEnum.MAX_STAKE,
    value: metadataStore.maxStake ?? "",
  },
  {
    type: GameInformationTypeEnum.MAX_WIN,
    value: metadataStore.maxWin ?? "",
  },
];

type SerializeGameTagsToBlocksParams = {
  gameData: Game;
  tags: Tag[];
  themeTags: ThemeTag[];
};

export const serializeGameTagsToBlocks = ({
  gameData,
  tags,
  themeTags,
}: SerializeGameTagsToBlocksParams): GameTagBlock[] => {
  const groupedServiceTags = groupBy(tags, "tagType");
  const groupedThemeTags = groupBy(themeTags, "tagType");

  const gameServicePrimaryTagIds = gameData.tagTypes;
  const gameThemePrimaryTagIds = gameData.themeTagTypes ?? [];
  const gameServiceSecondaryTags = gameData.tags ?? [];
  const gameThemeSecondaryTags = gameData.themeTags ?? [];

  // Make up service blocks
  const serviceBlocks = gameServicePrimaryTagIds?.map((primaryTagId) => {
    const allServiceTagsByTagType = new Map(
      groupedServiceTags[primaryTagId]?.map((tag) => [tag.id, tag]),
    );

    const selectedServiceTags = gameServiceSecondaryTags.reduce(
      (selected: TagOption[], tagId: string) => {
        const tag = allServiceTagsByTagType.get(tagId);
        if (tag) {
          return [
            ...selected,
            {
              id: tag.id,
              name: tag.name,
            },
          ];
        }

        return selected;
      },
      [],
    );

    return {
      id: uuidv4(),
      primaryTagId: primaryTagId,
      selectedTags: selectedServiceTags,
      tagType: GameTagType.SERVICE,
    };
  });

  // Make up theme blocks
  const themeBlocks = gameThemePrimaryTagIds
    ?.map((primaryTagId) => {
      const allThemeTagsByTagType = new Map(
        groupedThemeTags[primaryTagId]?.map((tag) => [tag.id, tag]),
      );

      const selectedThemeTags = gameThemeSecondaryTags.reduce(
        (selected: TagOption[], tagId: string) => {
          const tag = allThemeTagsByTagType.get(tagId);
          if (tag) {
            return [...selected, { id: tag.id, name: tag.name }];
          }
          return selected;
        },
        [],
      );

      return {
        id: uuidv4(),
        primaryTagId: primaryTagId,
        selectedTags: selectedThemeTags,
        tagType: GameTagType.THEME,
      };
    })
    .filter((block) => block.selectedTags.length > 0);

  return [...serviceBlocks, ...themeBlocks];
};

export const extractServiceAndThemeTags = (
  gameTagTypeBlocks: GameTagBlock[],
) => {
  const serviceBlocks = gameTagTypeBlocks?.filter(
    (block) => block.tagType === GameTagType.SERVICE,
  );
  const themeBlocks = gameTagTypeBlocks?.filter(
    (block) => block.tagType === GameTagType.THEME,
  );

  const servicePrimaryTagIds: string[] = serviceBlocks
    ?.map((block) => block.primaryTagId ?? "")
    .filter(Boolean);

  const themePrimaryTagIds: string[] = themeBlocks
    ?.map((block) => block.primaryTagId ?? "")
    .filter(Boolean);

  const serviceTags: string[] = serviceBlocks
    ?.flatMap((block) => block.selectedTags)
    .map((tag) => tag.id);
  const themeTags: string[] = themeBlocks
    ?.flatMap((block) => block.selectedTags)
    .map((tag) => tag.id);

  return { servicePrimaryTagIds, themePrimaryTagIds, serviceTags, themeTags };
};
